<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import SettingsSection from 'dashboard/components/SettingsSection.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EvolutionConnection from './EvolutionConnection.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('inboxes/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

const state = reactive({ baseUrl: '', apiKey: '', instanceName: '' });

const setDefaults = () => {
  state.baseUrl = props.inbox.base_url || '';
  state.apiKey = props.inbox.api_key || '';
  state.instanceName = props.inbox.instance_name || '';
};
watch(() => props.inbox, setDefaults, { immediate: true });

const isHttpUrl = value => /^https?:\/\//i.test(value || '');

const v$ = useVuelidate(
  {
    baseUrl: { required, isHttpUrl },
    apiKey: { required },
    instanceName: { required },
  },
  state
);

const errorFor = (field, message) => (v$.value[field]?.$error ? message : '');

const formErrors = computed(() => ({
  baseUrl: errorFor('baseUrl', t('INBOX_MGMT.ADD.EVOLUTION.BASE_URL.ERROR')),
  apiKey: errorFor('apiKey', t('INBOX_MGMT.ADD.EVOLUTION.API_KEY.ERROR')),
  instanceName: errorFor(
    'instanceName',
    t('INBOX_MGMT.ADD.EVOLUTION.INSTANCE_NAME.ERROR')
  ),
}));

async function updateCredentials() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: {
        base_url: state.baseUrl.trim(),
        api_key: state.apiKey.trim(),
        instance_name: state.instanceName.trim(),
      },
    });
    useAlert(t('INBOX_MGMT.EVOLUTION.CREDENTIALS.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
}
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="t('INBOX_MGMT.EVOLUTION.CONNECTION.TITLE')"
      :sub-title="t('INBOX_MGMT.EVOLUTION.CONNECTION.SUBTITLE')"
    >
      <EvolutionConnection :key="inbox.instance_name" :inbox-id="inbox.id" />
    </SettingsSection>

    <SettingsSection
      v-if="isSuperAdmin"
      :title="t('INBOX_MGMT.EVOLUTION.CREDENTIALS.TITLE')"
      :sub-title="t('INBOX_MGMT.EVOLUTION.CREDENTIALS.SUBTITLE')"
    >
      <form
        class="flex flex-col gap-4 max-w-3xl"
        @submit.prevent="updateCredentials"
      >
        <Input
          v-model="state.baseUrl"
          :label="t('INBOX_MGMT.ADD.EVOLUTION.BASE_URL.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.EVOLUTION.BASE_URL.PLACEHOLDER')"
          :message="formErrors.baseUrl"
          :message-type="formErrors.baseUrl ? 'error' : 'info'"
          @blur="v$.baseUrl.$touch"
        />
        <Input
          v-model="state.apiKey"
          type="password"
          :label="t('INBOX_MGMT.ADD.EVOLUTION.API_KEY.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.EVOLUTION.API_KEY.PLACEHOLDER')"
          :message="formErrors.apiKey"
          :message-type="formErrors.apiKey ? 'error' : 'info'"
          @blur="v$.apiKey.$touch"
        />
        <Input
          v-model="state.instanceName"
          :label="t('INBOX_MGMT.ADD.EVOLUTION.INSTANCE_NAME.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.EVOLUTION.INSTANCE_NAME.PLACEHOLDER')"
          :message="formErrors.instanceName"
          :message-type="formErrors.instanceName ? 'error' : 'info'"
          @blur="v$.instanceName.$touch"
        />
        <div>
          <NextButton
            type="submit"
            :label="t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </form>
    </SettingsSection>

    <SettingsSection
      v-if="isSuperAdmin"
      :title="t('INBOX_MGMT.EVOLUTION.WEBHOOK.TITLE')"
      :sub-title="t('INBOX_MGMT.EVOLUTION.WEBHOOK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>
</template>
